import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, Loader2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { validateTestCode } from '../services/codeVerification';

const TestCodeInput: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [testCode, setTestCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [assessmentType, setAssessmentType] = useState('emotional-memory-pattern');
  const [assessmentTitle, setAssessmentTitle] = useState('情绪记忆与构型');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const type = urlParams.get('type');
    const errorType = urlParams.get('error');
    
    // 设置测评类型，如果没有指定type参数，默认为情绪记忆测评（向后兼容）
    const assessmentTypeValue = type || 'emotional-memory-pattern';
    setAssessmentType(assessmentTypeValue);
    
    switch (assessmentTypeValue) {
      case 'big-five-personality':
        setAssessmentTitle('大五人格测评');
        break;

      case 'emotional-memory-pattern':
      default:
        setAssessmentTitle('情绪记忆与构型');
    }
    
    if (errorType === 'invalid') {
      setError('测评码无效，请重新输入');
    } else if (errorType === 'system') {
      setError('系统错误，请稍后重试');
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testCode.trim()) {
      setError('请输入测评码');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await validateTestCode(testCode.trim(), assessmentType as 'emotional-memory-pattern' | 'big-five-personality');
      
      if (result.isValid && result.code) {
        // validateTestCode函数内部已经保存了验证状态到localStorage
        // 不需要额外保存，统一使用checkVerificationStatus进行验证
        
        // 根据测试码的实际类型导航到对应的测评页面
        // 优先使用测试码本身的类型，确保用户进入正确的测评
        const codeAssessmentType = result.code.assessmentType || 'emotional-memory-pattern';
        
        // 检查测试码类型是否与用户期望的测评类型匹配
        if (codeAssessmentType !== assessmentType) {
          setError(`此测试码适用于${codeAssessmentType === 'big-five-personality' ? '大五人格测评' : '情绪记忆测评'}，请使用正确的测试码`);
          return;
        }
        
        if (codeAssessmentType === 'big-five-personality') {
          navigate('/big-five-personality');
        } else {
          navigate('/emotional-memory-pattern');
        }
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('验证失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3A7D78] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 w-full max-w-md border border-white/20"
      >
        {/* 返回按钮 */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          返回首页
        </button>

        {/* 标题 */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-[#F5B6C1]/20 p-3 rounded-full">
              <Lock className="h-8 w-8 text-[#F5B6C1]" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            输入测评码
          </h1>
          <p className="text-white/80 text-sm">
            请输入您的测评码以开始「{assessmentTitle}」测评
          </p>
        </div>

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <input
              type="text"
              value={testCode}
              onChange={(e) => {
                setTestCode(e.target.value.toUpperCase());
                setError('');
              }}
              placeholder="请输入测评码"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:border-[#F5B6C1] transition-colors"
              disabled={isLoading}
            />
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-300 text-sm mt-2"
              >
                {error}
              </motion.p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !testCode.trim()}
            className="w-full bg-[#F5B6C1] text-white py-3 rounded-lg font-medium hover:bg-[#F5B6C1]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                验证中...
              </>
            ) : (
              '开始测评'
            )}
          </button>
        </form>

        {/* 提示信息 */}
        <div className="mt-6 text-center">
          <p className="text-white/60 text-xs">
            没有测评码？请联系管理员获取
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default TestCodeInput;